import { EventEmitter } from '@spotify-internal/emitter';
import { DeviceType } from '../enums/device_type';
import { DeviceId } from '../utils/device_id';
import { interpolateUA } from './helpers/interpolate_ua';
import { capitalize } from './helpers/capitalize';
export class DeviceDescriptor extends EventEmitter {
    constructor(options) {
        var _a;
        super();
        /**
         * A user defined string that determines the version of the client.
         */
        this._platformVersion = '';
        /**
         * A map of the device specific metadata.
         */
        this._metadata = {};
        /**
         * The starting index for any manifest format prepending.
         */
        this._manifestPrependOffset = 0;
        if (!options) {
            throw new TypeError('Invalid argument `options`.');
        }
        if (!options.id) {
            throw new TypeError('Argument `options.id` is required.');
        }
        if (!options.name || !options.model || !options.type) {
            throw new TypeError('Incomplete `options` value.');
        }
        this._id = options.id;
        this._type = options.type;
        this._brand = options.brand;
        this._model = options.model;
        this._name = options.name;
        this._platformIdentifier = options.platform_identifier;
        this._platformVersion = options.platform_version || '';
        this._metadata = options.metadata || {};
        this._capabilities = options.capabilities || {
            manifest_formats: [],
        };
        // Set the manifest prepend offset based on the initial number of "default"
        // manifests
        this._manifestPrependOffset =
            ((_a = this._capabilities.manifest_formats) === null || _a === void 0 ? void 0 : _a.length) || 0;
    }
    static create(deviceInfo) {
        return new DeviceDescriptor({
            id: DeviceId.get(deviceInfo.id, deviceInfo.randomizeId),
            model: deviceInfo.model ||
                interpolateUA('harmony-{{name}}.{{version}}-{{platform}}').toLowerCase(),
            name: deviceInfo.nameTemplate
                ? interpolateUA(deviceInfo.nameTemplate)
                : deviceInfo.name ||
                    capitalize(interpolateUA('Spotify ({{name}}/{{platform}})')),
            type: deviceInfo.type || DeviceType.COMPUTER,
            brand: deviceInfo.brand || 'SpotifyHarmonyGeneric',
            platform_identifier: deviceInfo.platform_identifier,
            platform_version: deviceInfo.platform_version,
            metadata: deviceInfo.metadata || {},
            capabilities: deviceInfo.capabilities || {},
        });
    }
    getId() {
        return this._id;
    }
    setCapability(key, value) {
        switch (key) {
            case 'manifest_formats':
                throw new TypeError('Cannot change capability "manifest_formats"');
            default:
                this._capabilities[key] = value;
                this.emit("descriptor_changed" /* DEVICE_DESCRIPTOR_CHANGED */, {
                    descriptor: this.toJSON(),
                });
                return true;
        }
    }
    getCapability(key) {
        switch (key) {
            case 'manifest_formats':
                throw new TypeError('Use `descriptor.getManifestFormats()`.');
            default:
                return this._capabilities[key];
        }
    }
    setName(name) {
        this._name = name;
        this.emit("descriptor_changed" /* DEVICE_DESCRIPTOR_CHANGED */, {
            descriptor: this.toJSON(),
        });
        return true;
    }
    getName() {
        return this._name;
    }
    getPlatformIdentifier() {
        return this._platformIdentifier || `Partner ${this._brand} ${this._model}`;
    }
    getPlatformVersion() {
        return this._platformVersion;
    }
    getManifestFormats() {
        return this._capabilities.manifest_formats || [];
    }
    appendManifestFormat(...formats) {
        const manifestFormats = this._capabilities.manifest_formats;
        if (!Array.isArray(manifestFormats)) {
            return;
        }
        manifestFormats.push(...formats);
    }
    prependManifestFormat(...formats) {
        const manifestFormats = this._capabilities.manifest_formats;
        if (!Array.isArray(manifestFormats)) {
            return;
        }
        manifestFormats.splice(this._manifestPrependOffset, 0, ...formats);
    }
    toJSON() {
        return {
            brand: this._brand,
            capabilities: Object.assign({}, this._capabilities),
            id: this._id,
            metadata: Object.assign({}, this._metadata),
            model: this._model,
            name: this._name,
            platform_identifier: this._platformIdentifier,
            platform_version: this._platformVersion,
            type: this._type,
        };
    }
    toTrackPlaybackDeviceInfo() {
        return {
            brand: this._brand,
            capabilities: Object.assign({}, this._capabilities),
            device_id: this._id,
            device_type: this._type,
            metadata: Object.assign({}, this._metadata),
            model: this._model,
            name: this._name,
            platform_identifier: this._platformIdentifier,
        };
    }
}
//# sourceMappingURL=device_descriptor.js.map